import { forwardRef, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { RewardMilestone } from '@/components/RewardMilestone';
import { usePersonalStreak } from '@/hooks/usePersonalStreak';
import { usePartnerSlots } from '@/hooks/usePartnerSlots';
import { Skeleton } from '@/components/ui/skeleton';

const MILESTONES = [
  { days: 7, key: 'reward7days' },
  { days: 14, key: 'reward14days' },
  { days: 30, key: 'reward30days' },
  { days: 60, key: 'reward60days' },
  { days: 100, key: 'reward100days' },
  { days: 182, key: 'reward6months' },
  { days: 300, key: 'reward300days' },
  { days: 365, key: 'reward365days' },
] as const;

interface RewardsSectionProps {
  onModalOpen?: () => void;
  onModalClose?: () => void;
}

export const RewardsSection = forwardRef<HTMLElement, RewardsSectionProps>(
  function RewardsSection({ onModalOpen, onModalClose }, ref) {
    const { t } = useLanguage();
    const { group } = useAuth();
    const { data: personalStreakData, isLoading } = usePersonalStreak();
    const { data: partnerSlots } = usePartnerSlots();

    // Personal streaks
    const personalLongestStreak = personalStreakData?.personalLongestStreak ?? 0;
    const personalCurrentStreak = personalStreakData?.personalCurrentStreak ?? 0;
    
    // Find the next milestone (first one not yet unlocked by longest streak)
    const nextMilestoneDays = MILESTONES.find(m => personalLongestStreak < m.days)?.days;

    // Check if user has an active challenge (for partner benefits)
    const hasActiveChallenge = group && 
      group.status === 'active' && 
      new Date(group.challenge_end) > new Date();

    return (
      <section ref={ref} id="rewards" className="py-8 border-t border-border">
        {/* Section Header */}
        <div className="px-6 pb-4 text-center max-w-lg mx-auto">
          <h2 className="text-xl font-serif font-bold mb-2">{t('rewardsTitle')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('rewardsSubtitle')}
          </p>
        </div>

        {/* Milestones */}
        <div className="px-6 space-y-3 max-w-lg mx-auto">
          {isLoading ? (
            <>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-20 w-full rounded-lg" />
              ))}
            </>
          ) : (
            MILESTONES.map((milestone) => (
              <RewardMilestone
                key={milestone.days}
                days={milestone.days}
                translationKey={milestone.key}
                longestStreak={personalLongestStreak}
                currentStreak={personalCurrentStreak}
                isNextMilestone={milestone.days === nextMilestoneDays}
                partnerSlot={hasActiveChallenge ? partnerSlots?.get(milestone.days) : undefined}
                onModalOpen={onModalOpen}
                onModalClose={onModalClose}
              />
            ))
          )}
        </div>
      </section>
    );
  }
);
